import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { pointOfSaleAPI, routerAPI, planAPI } from '../services/api';
import { Store, Plus, ArrowLeft, Edit2, Trash2, X, DollarSign, Package } from 'lucide-react';
import { Alert } from '../components/Modal';
import { getRouterColor, getRouterName } from '../utils/routerColors';

const PointsOfSalePage = () => {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const [activeTab, setActiveTab] = useState('listar');
  const [pointsOfSale, setPointsOfSale] = useState([]);
  const [routers, setRouters] = useState([]);
  const [selectedRouterFilter, setSelectedRouterFilter] = useState('all');
  const [showSuspended, setShowSuspended] = useState(false); // DEPRECATED - usar selectedStatusFilter
  const [selectedStatusFilter, setSelectedStatusFilter] = useState('active'); // 'active' | 'suspended' | 'all'
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    router_id: '',
    name: '',
    assigned_plans: []
  });
  const [selectedPlans, setSelectedPlans] = useState({});
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [editingPos, setEditingPos] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [alert, setAlert] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [posRes, routersRes] = await Promise.all([
        pointOfSaleAPI.getAll(),
        routerAPI.getAll()
      ]);
      setPointsOfSale(posRes.data);
      setRouters(routersRes.data);
      
      if (routersRes.data.length > 0) {
        setFormData(prev => ({ ...prev, router_id: routersRes.data[0].id }));
        loadPlans(routersRes.data[0].id);
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadPlans = async (routerId) => {
    try {
      const plansRes = await planAPI.getAll(routerId);
      setPlans(plansRes.data);
      
      // Resetear selección de planes
      setSelectedPlans({});
    } catch (error) {
      console.error('Error loading plans:', error);
    }
  };

  const handleRouterChange = (routerId) => {
    setFormData({ ...formData, router_id: routerId, assigned_plans: [] });
    setSelectedPlans({});
    loadPlans(routerId);
  };

  const handlePlanToggle = (planId, planName) => {
    setSelectedPlans(prev => {
      const newSelected = { ...prev };
      if (newSelected[planId]) {
        delete newSelected[planId];
      } else {
        newSelected[planId] = {
          plan_id: planId,
          plan_name: planName,
          price: ''
        };
      }
      return newSelected;
    });
  };

  const handlePriceChange = (planId, price) => {
    setSelectedPlans(prev => ({
      ...prev,
      [planId]: {
        ...prev[planId],
        price: price
      }
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      // Validaciones
      if (!formData.router_id) {
        setError('Debe seleccionar un router');
        setSubmitting(false);
        return;
      }

      if (!formData.name || formData.name.trim() === '') {
        setError('Debe especificar un nombre para el punto de venta');
        setSubmitting(false);
        return;
      }

      const assignedPlans = Object.values(selectedPlans);
      
      if (assignedPlans.length === 0) {
        setError('Debe seleccionar al menos un plan');
        setSubmitting(false);
        return;
      }

      // Verificar que todos los precios estén llenos
      const hasEmptyPrice = assignedPlans.some(plan => !plan.price || parseFloat(plan.price) <= 0);
      if (hasEmptyPrice) {
        setError('Todos los planes seleccionados deben tener un precio válido');
        setSubmitting(false);
        return;
      }

      // Convertir precios a números
      const plansWithPrices = assignedPlans.map(plan => ({
        ...plan,
        price: parseFloat(plan.price)
      }));

      const posPayload = {
        router_id: formData.router_id,
        name: formData.name,
        assigned_plans: plansWithPrices
      };

      await pointOfSaleAPI.create(posPayload);
      
      // Resetear formulario
      setFormData({
        router_id: routers[0]?.id || '',
        name: '',
        assigned_plans: []
      });
      setSelectedPlans({});
      
      setActiveTab('listar');
      loadData();
      
      setAlert({
        type: 'success',
        title: 'Punto de Venta Creado',
        message: 'El punto de venta ha sido creado exitosamente con los planes asignados.'
      });
    } catch (err) {
      console.error('Error creating point of sale:', err);
      const errorMessage = err.response?.data?.detail || err.message || 'Error al crear punto de venta';
      setError(errorMessage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = async (pos) => {
    setEditingPos(pos);
    
    // Cargar planes del router
    await loadPlans(pos.router_id);
    
    // Pre-seleccionar planes asignados
    const preSelected = {};
    pos.assigned_plans.forEach(plan => {
      preSelected[plan.plan_id] = plan;
    });
    setSelectedPlans(preSelected);
    
    setShowEditModal(true);
  };

  const handleUpdateSubmit = async (e) => {
    e.preventDefault();
    setError('');

    try {
      const assignedPlans = Object.values(selectedPlans);
      
      console.log('🔍 DEBUG - Planes seleccionados:', selectedPlans);
      console.log('🔍 DEBUG - Array de planes:', assignedPlans);
      console.log('🔍 DEBUG - Cantidad de planes:', assignedPlans.length);
      
      if (assignedPlans.length === 0) {
        setError('Debe seleccionar al menos un plan');
        return;
      }

      const hasEmptyPrice = assignedPlans.some(plan => !plan.price || parseFloat(plan.price) <= 0);
      if (hasEmptyPrice) {
        setError('Todos los planes seleccionados deben tener un precio válido');
        return;
      }

      // Solo enviar los campos necesarios (plan_id, plan_name, price)
      // NO enviar logo_url ni otros campos pesados
      const plansWithPrices = assignedPlans.map(plan => ({
        plan_id: plan.plan_id,
        plan_name: plan.plan_name || plan.name,
        price: parseFloat(plan.price)
      }));

      console.log('💾 DEBUG - Enviando al backend:', plansWithPrices);

      await pointOfSaleAPI.update(editingPos.id, {
        assigned_plans: plansWithPrices
      });

      setShowEditModal(false);
      setEditingPos(null);
      setSelectedPlans({});
      loadData();
      
      setAlert({
        type: 'success',
        title: 'Punto de Venta Actualizado',
        message: 'Los cambios han sido guardados exitosamente.'
      });
    } catch (err) {
      console.error('Error updating point of sale:', err);
      setError(err.response?.data?.detail || 'Error al actualizar punto de venta');
    }
  };

  const handleDelete = async (posId, posName) => {
    try {
      // Obtener el punto de venta para saber si está activo o suspendido
      const pos = pointsOfSale.find(p => p.id === posId);
      const isReactivating = !pos.is_active;
      
      // Si está reactivando, hacerlo directamente sin confirmación
      if (isReactivating) {
        const response = await pointOfSaleAPI.delete(posId);
        loadData();
        setAlert({
          type: 'success',
          title: 'Punto de Venta Reactivado',
          message: `El TPV "${posName}" ha sido reactivado exitosamente.`
        });
        return;
      }
      
      // Si está suspendiendo, pedir confirmación primero
      const confirmMessage = `¿Está seguro de SUSPENDER el punto de venta "${posName}"?\n\n` +
        `⚠️ Los vendedores asignados no podrán generar fichas mientras esté suspendido.\n` +
        `✅ Los datos se conservarán y podrás reactivarlo después.`;
      
      if (!window.confirm(confirmMessage)) {
        // Si cancela, NO hacer nada
        return;
      }
      
      // Si confirma, AHORA SÍ ejecutar la suspensión
      const response = await pointOfSaleAPI.delete(posId);
      loadData();
      setAlert({
        type: 'success',
        title: 'Punto de Venta Suspendido',
        message: response.data.message || `El TPV "${posName}" ha sido suspendido.`
      });
    } catch (err) {
      console.error('Error en suspender/reactivar:', err);
      setAlert({
        type: 'error',
        title: 'Error',
        message: err.response?.data?.detail || 'No se pudo realizar la operación.'
      });
    }
  };

  return (
    <div className={`min-h-screen transition-colors duration-300 ${
      isDark ? 'bg-gray-900' : 'bg-gray-100'
    }`}>
      {/* Header */}
      <header className={`shadow-sm transition-colors duration-300 ${
        isDark ? 'bg-gray-800 border-b border-gray-700' : 'bg-white'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/dashboard')}
                className={`p-2 rounded-lg transition-colors ${
                  isDark ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-gray-100 text-gray-900'
                }`}
              >
                <ArrowLeft className="w-6 h-6" />
              </button>
              <div>
                <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Puntos de Venta</h1>
                <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Gestiona tus zonas de venta (TPV)</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tabs */}
        <div className={`flex gap-2 mb-6 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
          <button
            onClick={() => setActiveTab('listar')}
            style={{
              color: activeTab === 'listar' 
                ? '#2563eb' 
                : isDark ? '#d1d5db' : '#4b5563'
            }}
            className={`px-6 py-3 font-semibold transition-colors border-b-2 ${
              activeTab === 'listar'
                ? 'border-blue-600'
                : 'border-transparent hover:opacity-80'
            }`}
          >
            <div className="flex items-center gap-2">
              <Store className="w-5 h-5" />
              Ver Puntos de Venta
            </div>
          </button>
          <button
            onClick={() => setActiveTab('crear')}
            style={{
              color: activeTab === 'crear' 
                ? '#2563eb' 
                : isDark ? '#d1d5db' : '#4b5563'
            }}
            className={`px-6 py-3 font-semibold transition-colors border-b-2 ${
              activeTab === 'crear'
                ? 'border-blue-600'
                : 'border-transparent hover:opacity-80'
            }`}
          >
            <div className="flex items-center gap-2">
              <Plus className="w-5 h-5" />
              Crear Punto de Venta
            </div>
          </button>
        </div>

        {/* Crear TPV Tab */}
        {activeTab === 'crear' && (
          <div className="bg-white rounded-xl shadow-sm p-8">
            <h2 className="text-2xl font-bold mb-6">Crear Nuevo Punto de Venta</h2>
            
            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Router Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Router / Mikrotik *
                </label>
                <select
                  required
                  value={formData.router_id}
                  onChange={(e) => handleRouterChange(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Selecciona un router</option>
                  {routers.map((router) => (
                    <option key={router.id} value={router.id}>
                      {router.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Nombre del TPV */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nombre del Punto de Venta *
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value.toUpperCase() })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Ej: ESCUELA, CANCHA, PARQUE, etc."
                />
                <p className="text-xs text-gray-500 mt-1">Zona o ubicación donde se venderán las fichas</p>
              </div>

              {/* Planes Asignados */}
              {plans.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Planes a Vender en este Punto *
                  </label>
                  <div className="border border-gray-300 rounded-lg divide-y divide-gray-200">
                    {plans.map((plan) => (
                      <div key={plan.id} className="p-4 hover:bg-gray-50 transition-colors">
                        <div className="flex items-start gap-4">
                          <input
                            type="checkbox"
                            id={`plan-${plan.id}`}
                            checked={!!selectedPlans[plan.id]}
                            onChange={() => handlePlanToggle(plan.id, plan.name)}
                            className="mt-1 w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                          />
                          <div className="flex-1">
                            <label htmlFor={`plan-${plan.id}`} className="font-semibold text-gray-900 cursor-pointer">
                              {plan.name}
                            </label>
                            <p className="text-sm text-gray-600">
                              Duración: {plan.duration_minutes} min | Velocidad: {plan.rate_limit}
                            </p>
                          </div>
                          {selectedPlans[plan.id] && (
                            <div className="w-32">
                              <div className="relative">
                                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                                <input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  placeholder="Precio"
                                  value={selectedPlans[plan.id]?.price || ''}
                                  onChange={(e) => handlePriceChange(plan.id, e.target.value)}
                                  className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    Selecciona los planes que se venderán en este punto y asigna el precio de venta para cada uno
                  </p>
                </div>
              )}

              {plans.length === 0 && formData.router_id && (
                <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg text-yellow-800">
                  No hay planes disponibles para este router. Crea planes primero en el módulo de Planes.
                </div>
              )}

              {/* Submit Buttons */}
              <div className="flex gap-4 pt-6">
                <button
                  type="button"
                  onClick={() => {
                    setFormData({
                      router_id: routers[0]?.id || '',
                      name: '',
                      assigned_plans: []
                    });
                    setSelectedPlans({});
                    setError('');
                  }}
                  className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-semibold"
                >
                  Limpiar
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 font-semibold"
                >
                  {submitting ? 'Creando...' : 'Crear Punto de Venta'}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Listar TPVs Tab */}
        {activeTab === 'listar' && (
          <>
            {loading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
              </div>
            ) : pointsOfSale.length === 0 ? (
              <div className="bg-white rounded-xl shadow-sm p-12 text-center">
                <Store className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">No hay puntos de venta</h3>
                <p className="text-gray-600 mb-6">Crea tu primer punto de venta</p>
                <button
                  onClick={() => setActiveTab('crear')}
                  className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Crear Punto de Venta
                </button>
              </div>
            ) : (
              <>
                {/* Tabs de filtrado por router */}
                {routers.length > 1 && (
                  <div className="mb-6 flex flex-wrap gap-2">
                    <button
                      onClick={() => setSelectedRouterFilter('all')}
                      className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                        selectedRouterFilter === 'all'
                          ? 'bg-blue-600 text-white'
                          : isDark ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      Todos ({pointsOfSale.length})
                    </button>
                    {routers.map((router) => {
                      const routerPOS = pointsOfSale.filter(p => p.router_id === router.id);
                      const colors = getRouterColor(router.id, routers);
                      return (
                        <button
                          key={router.id}
                          onClick={() => setSelectedRouterFilter(router.id)}
                          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                            selectedRouterFilter === router.id
                              ? `${colors.badge} text-white`
                              : isDark ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          {router.name} ({routerPOS.length})
                        </button>
                      );
                    })}
                  </div>
                )}

              {/* Tabs de filtrado por estado */}
              <div className="mb-6 flex flex-wrap gap-2">
                <button
                  onClick={() => setSelectedStatusFilter('active')}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                    selectedStatusFilter === 'active'
                      ? 'bg-green-600 text-white'
                      : isDark ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  🟢 Activos ({pointsOfSale.filter(p => p.is_active !== false).length})
                </button>
                <button
                  onClick={() => setSelectedStatusFilter('suspended')}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                    selectedStatusFilter === 'suspended'
                      ? 'bg-orange-600 text-white'
                      : isDark ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  🔴 Suspendidos ({pointsOfSale.filter(p => p.is_active === false).length})
                </button>
                <button
                  onClick={() => setSelectedStatusFilter('all')}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                    selectedStatusFilter === 'all'
                      ? 'bg-blue-600 text-white'
                      : isDark ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  📋 Todos ({pointsOfSale.length})
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {pointsOfSale
                  .filter(pos => {
                    const matchesRouter = selectedRouterFilter === 'all' || pos.router_id === selectedRouterFilter;
                    // Filtro por estado
                    const matchesStatus = selectedStatusFilter === 'all' 
                      ? true 
                      : selectedStatusFilter === 'active' 
                        ? pos.is_active !== false 
                        : pos.is_active === false;
                    return matchesRouter && matchesStatus;
                  })
                  .map((pos) => {
                  const router = routers.find(r => r.id === pos.router_id);
                  const colors = getRouterColor(pos.router_id, routers);
                  const routerName = getRouterName(pos.router_id, routers);
                  
                  return (
                    <div
                      key={pos.id}
                      className={`bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow overflow-hidden border-2 ${colors.border} ${!pos.is_active ? 'opacity-50' : ''}`}
                      style={!pos.is_active ? {filter: 'grayscale(50%)'} : {}}
                    >
                      {!pos.is_active && (
                        <div className="absolute top-2 right-2 z-10 bg-orange-500 text-white text-xs px-2 py-1 rounded-full font-semibold">
                          SUSPENDIDO
                        </div>
                      )}
                      {/* Badge de router */}
                      <div className={`${colors.badge} text-white text-xs font-semibold px-3 py-1`}>
                        {routerName}
                      </div>
                      
                      <div className="p-6">
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex items-center gap-3">
                            <div className={`p-2 ${colors.bg} rounded-lg`}>
                              <Store className={`w-6 h-6 ${colors.text}`} />
                            </div>
                            <div>
                              <h3 className="text-xl font-bold text-gray-900">{pos.name}</h3>
                            </div>
                          </div>
                        </div>
                        
                        <div className="space-y-2 mb-4">
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <Package className="w-4 h-4" />
                            <span>{pos.assigned_plans.filter(plan => !plan.plan_name.includes('DELETED')).length} planes asignados</span>
                          </div>
                        </div>

                        {/* Planes asignados */}
                        <div className="mb-4">
                          <p className="text-xs font-semibold text-gray-700 mb-2">PLANES:</p>
                          <div className="space-y-1">
                            {pos.assigned_plans
                              .filter(plan => !plan.plan_name.includes('DELETED'))
                              .slice(0, 3)
                              .map((plan, idx) => (
                              <div key={idx} className="flex justify-between text-sm">
                                <span className="text-gray-700">{plan.plan_name}</span>
                                <span className="font-semibold text-green-600">${plan.price}</span>
                              </div>
                            ))}
                            {pos.assigned_plans.filter(plan => !plan.plan_name.includes('DELETED')).length > 3 && (
                              <p className="text-xs text-gray-500">+{pos.assigned_plans.filter(plan => !plan.plan_name.includes('DELETED')).length - 3} más...</p>
                            )}
                          </div>
                        </div>

                        {/* Acciones */}
                        <div className="flex gap-2 pt-4 border-t border-gray-200">
                          <button
                            onClick={() => handleEdit(pos)}
                            className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors text-sm font-semibold"
                          >
                            <Edit2 className="w-4 h-4" />
                            Editar
                          </button>
                          <button
                            onClick={() => handleDelete(pos.id, pos.name)}
                            className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg transition-colors text-sm font-semibold ${
                              !pos.is_active 
                                ? 'bg-green-50 text-green-700 hover:bg-green-100' 
                                : 'bg-orange-50 text-orange-700 hover:bg-orange-100'
                            }`}
                            title={!pos.is_active ? "Reactivar punto de venta" : "Suspender punto de venta"}
                          >
                            <Trash2 className="w-4 h-4" />
                            {!pos.is_active ? 'Reactivar' : 'Suspender'}
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              </>
            )}
          </>
        )}
      </div>

      {/* Edit Modal */}
      {showEditModal && editingPos && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h3 className="text-xl font-bold">Editar: {editingPos.name}</h3>
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setEditingPos(null);
                  setSelectedPlans({});
                  setError('');
                }}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleUpdateSubmit} className="p-6">
              {error && (
                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  {error}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Planes Asignados
                </label>
                <div className="border border-gray-300 rounded-lg divide-y divide-gray-200">
                  {plans.map((plan) => (
                    <div key={plan.id} className="p-4 hover:bg-gray-50 transition-colors">
                      <div className="flex items-start gap-4">
                        <input
                          type="checkbox"
                          id={`edit-plan-${plan.id}`}
                          checked={!!selectedPlans[plan.id]}
                          onChange={() => handlePlanToggle(plan.id, plan.name)}
                          className="mt-1 w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                        <div className="flex-1">
                          <label htmlFor={`edit-plan-${plan.id}`} className="font-semibold text-gray-900 cursor-pointer">
                            {plan.name}
                          </label>
                          <p className="text-sm text-gray-600">
                            {plan.duration_minutes} min | {plan.rate_limit}
                          </p>
                        </div>
                        {selectedPlans[plan.id] && (
                          <div className="w-32">
                            <div className="relative">
                              <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                placeholder="Precio"
                                value={selectedPlans[plan.id]?.price || ''}
                                onChange={(e) => handlePriceChange(plan.id, e.target.value)}
                                className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex gap-4 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    setEditingPos(null);
                    setSelectedPlans({});
                    setError('');
                  }}
                  className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-semibold"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold"
                >
                  Guardar Cambios
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Alert Component */}
      {alert && (
        <Alert
          type={alert.type}
          title={alert.title}
          message={alert.message}
          onClose={() => setAlert(null)}
        />
      )}
    </div>
  );
};

export default PointsOfSalePage;
